import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ProjectHeader from './components/ProjectHeader';
import ChallengeCard from './components/ChallengeCard';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import CoverImageUpload from '../challenge-creation/components/CoverImageUpload';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { getCachedSignedUrl, makeSafeObjectPath, uploadToBucket } from '../../utils/storage';
import { compressImageUnderBytes } from '../../utils/imageCompression';
import Seo from '../../components/Seo';
import AICoverImageWizard from '../project-creation/components/AICoverImageWizard';
import AIChallengeRecommendWizard from '../project-creation/components/AIChallengeRecommendWizard';

const ProjectDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams?.get('id') || '';
  const { user } = useAuth();

  const formatYmd = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const [isOwner, setIsOwner] = useState(true);
  const [project, setProject] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    coverImage: null,
    coverImageFile: null,
    tagsText: '',
    isPublic: false,
  });
  const [editErrors, setEditErrors] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAICoverOpen, setIsAICoverOpen] = useState(false);
  const [isAIChallengeOpen, setIsAIChallengeOpen] = useState(false);
  const [isAIChallengeCreating, setIsAIChallengeCreating] = useState(false);
  const [aiChallengeError, setAiChallengeError] = useState('');

  const parseTags = (raw) => {
    const items = String(raw || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    return Array.from(new Set(items));
  };

  const loadProject = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);

    const { data: p, error: pErr } = await supabase
      .from('ch_projects')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (pErr || !p) {
      setProject(null);
      setChallenges([]);
      setIsLoading(false);
      return;
    }

    const mine = !!user?.id && p?.owner_id === user.id;
    const isPublic = !!p?.is_public;
    if (!mine && !isPublic) {
      setProject(null);
      setChallenges([]);
      setIsLoading(false);
      return;
    }

    setIsOwner(mine);

    const coverUrl = p?.cover_image_path
      ? await getCachedSignedUrl({ bucket: 'project-covers', path: p.cover_image_path })
      : '';

    let challengesVm = [];

    if (mine) {
      const { data: cList, error: cErr } = await supabase.rpc('ch_list_challenges_with_stats', {
        p_project_id: p.id,
      });

      if (cErr) {
        setProject({
          id: p.id,
          title: p.title,
          description: p.description,
          totalChallenges: 0,
          completedChallenges: 0,
          totalProofs: 0,
          averageStars: 0,
          createdDate: p.created_at,
          coverImage: coverUrl,
          coverImageAlt: p.cover_image_alt || p.title,
          coverImagePath: p.cover_image_path || '',
          tags: Array.isArray(p.tags) ? p.tags : [],
        });
        setChallenges([]);
        setIsLoading(false);
        return;
      }

      challengesVm = await Promise.all(
        (cList || []).map(async (c) => {
          const cCoverUrl = c?.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'challenge-covers', path: c.cover_image_path })
            : '';

          const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
          const totalGoals = c.total_goals ?? c.target_count ?? 0;
          const completedProofs = c.completed_proofs ?? 0;

          return {
            id: c.id,
            title: c.title,
            description: c.description,
            coverImage: cCoverUrl,
            coverImageAlt: c.cover_image_alt || c.title,
            goalType,
            totalGoals,
            completedProofs,
            totalLikes: c.total_likes ?? 0,
            averageStars: Number(c.average_stars ?? 0),
            estimatedTime: '',
            createdDate: formatYmd(c.created_at),
            isCompleted: !!c.is_completed,
            isLocked: false,
            recentProofs: [],
          };
        })
      );
    } else {
      const { data: cRows, error: cErr } = await supabase
        .from('ch_challenges')
        .select('id, title, description, goal_type, target_count, total_goals, cover_image_path, cover_image_alt, created_at')
        .eq('project_id', p.id)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (cErr) {
        setProject(null);
        setChallenges([]);
        setIsLoading(false);
        return;
      }

      const ids = (cRows || []).map((x) => x.id);
      const { data: proofRows } = ids.length > 0
        ? await supabase
          .from('ch_proofs')
          .select('id, challenge_id, rating')
          .eq('user_id', p.owner_id)
          .eq('is_public', true)
          .in('challenge_id', ids)
        : { data: [] };

      const proofIds = (proofRows || []).map((r) => r.id).filter(Boolean);
      const { data: likeRows } = proofIds.length > 0
        ? await supabase
          .from('ch_proof_likes')
          .select('proof_id')
          .in('proof_id', proofIds)
        : { data: [] };

      const likeCountByProof = new Map();
      (likeRows || []).forEach((lk) => {
        likeCountByProof.set(lk.proof_id, (likeCountByProof.get(lk.proof_id) || 0) + 1);
      });

      const proofCountByChallenge = new Map();
      const sumStarsByChallenge = new Map();
      const likeCountByChallenge = new Map();
      (proofRows || []).forEach((r) => {
        proofCountByChallenge.set(r.challenge_id, (proofCountByChallenge.get(r.challenge_id) || 0) + 1);
        sumStarsByChallenge.set(r.challenge_id, (sumStarsByChallenge.get(r.challenge_id) || 0) + Number(r.rating || 0));
        likeCountByChallenge.set(
          r.challenge_id,
          (likeCountByChallenge.get(r.challenge_id) || 0) + (likeCountByProof.get(r.id) || 0)
        );
      });

      challengesVm = await Promise.all(
        (cRows || []).map(async (c) => {
          const cCoverUrl = c?.cover_image_path
            ? await getCachedSignedUrl({ bucket: 'challenge-covers', path: c.cover_image_path })
            : '';

          const goalType = c.goal_type === 'thirty-day' ? 'thirty_day' : c.goal_type;
          const totalGoals = goalType === 'thirty_day'
            ? 30
            : Number(c.total_goals ?? c.target_count ?? 0);

          const completedProofs = proofCountByChallenge.get(c.id) || 0;
          const sumStars = sumStarsByChallenge.get(c.id) || 0;
          const avg = completedProofs > 0 ? sumStars / completedProofs : 0;

          return {
            id: c.id,
            title: c.title,
            description: c.description,
            coverImage: cCoverUrl,
            coverImageAlt: c.cover_image_alt || c.title,
            goalType,
            totalGoals,
            completedProofs,
            totalLikes: likeCountByChallenge.get(c.id) || 0,
            averageStars: Number(avg || 0),
            estimatedTime: '',
            createdDate: formatYmd(c.created_at),
            isCompleted: totalGoals > 0 ? completedProofs >= totalGoals : false,
            isLocked: false,
            recentProofs: [],
          };
        })
      );
    }

    const totalChallenges = challengesVm.length;
    const completedChallenges = challengesVm.filter((x) => x.isCompleted).length;
    const totalProofs = challengesVm.reduce((sum, x) => sum + (x.completedProofs || 0), 0);
    const weightedStars = challengesVm.reduce(
      (acc, x) => acc + (Number(x.averageStars || 0) * (x.completedProofs || 0)),
      0
    );
    const averageStars = totalProofs > 0 ? weightedStars / totalProofs : 0;

    const projectVm = {
      id: p.id,
      title: p.title,
      description: p.description,
      totalChallenges,
      completedChallenges,
      totalProofs,
      averageStars,
      createdDate: p.created_at,
      coverImage: coverUrl,
      coverImageAlt: p.cover_image_alt || p.title,
      coverImagePath: p.cover_image_path || '',
      tags: Array.isArray(p.tags) ? p.tags : [],
      isPublic: !!p.is_public,
    };

    setProject(projectVm);
    setChallenges(challengesVm);
    setIsLoading(false);
  }, [projectId, user?.id]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < (arr?.length || 0); i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const handleDeleteProject = async () => {
    if (!user?.id || !project?.id) return;

    setIsDeleting(true);
    setEditErrors((prev) => ({ ...prev, form: '' }));

    try {
      const { data: projRow, error: projErr } = await supabase
        .from('ch_projects')
        .select('id, owner_id, cover_image_path')
        .eq('id', project.id)
        .eq('owner_id', user.id)
        .maybeSingle();

      if (projErr || !projRow) {
        setEditErrors((prev) => ({ ...prev, form: projErr?.message || '목표를 찾을 수 없습니다.' }));
        return;
      }

      const { data: challengeRows, error: chErr } = await supabase
        .from('ch_challenges')
        .select('id, cover_image_path')
        .eq('project_id', projRow.id)
        .eq('owner_id', user.id);

      if (chErr) {
        setEditErrors((prev) => ({ ...prev, form: chErr?.message || '관련 챌린지를 불러오지 못했습니다.' }));
        return;
      }

      const challengeIds = (challengeRows || []).map((c) => c.id);

      const { data: proofRows, error: prErr } = challengeIds.length > 0
        ? await supabase
          .from('ch_proofs')
          .select('id, image_path')
          .in('challenge_id', challengeIds)
          .eq('user_id', user.id)
        : { data: [], error: null };

      if (prErr) {
        setEditErrors((prev) => ({ ...prev, form: prErr?.message || '관련 인증을 불러오지 못했습니다.' }));
        return;
      }

      const projectCoverPaths = projRow.cover_image_path ? [projRow.cover_image_path] : [];
      const challengeCoverPaths = (challengeRows || []).map((c) => c.cover_image_path).filter(Boolean);
      const proofImagePaths = (proofRows || []).map((p) => p.image_path).filter(Boolean);

      for (const batch of chunk(projectCoverPaths, 100)) {
        await supabase.storage.from('project-covers').remove(batch);
      }
      for (const batch of chunk(challengeCoverPaths, 100)) {
        await supabase.storage.from('challenge-covers').remove(batch);
      }
      for (const batch of chunk(proofImagePaths, 100)) {
        await supabase.storage.from('proof-images').remove(batch);
      }

      const { error: delErr } = await supabase
        .from('ch_projects')
        .delete()
        .eq('id', projRow.id)
        .eq('owner_id', user.id);

      if (delErr) {
        setEditErrors((prev) => ({ ...prev, form: delErr?.message || '목표 삭제에 실패했습니다.' }));
        return;
      }

      setIsDeleteConfirmOpen(false);
      setIsEditOpen(false);
      navigate('/project-dashboard');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChallengeClick = (challengeId) => {
    navigate('/challenge-detail', { state: { challengeId } });
  };

  const handleAddChallenge = () => {
    navigate('/challenge-creation', { state: { projectId } });
  };

  const normalizeGoalType = (raw) => (raw === 'thirty-day' ? 'thirty_day' : raw);

  const computeTotalGoals = (goalType, parameters) => {
    if (goalType === 'fixed') return Number(parameters?.targetCount || 0);
    if (goalType === 'split') return Number(parameters?.totalGoal || 0);
    if (goalType === 'thirty_day') return 30;
    return 0;
  };

  const handleAIChallengeSelect = useCallback(async (selected) => {
    if (!selected) return;
    if (!user?.id) {
      navigate('/login-registration');
      return;
    }
    if (!project?.id) return;

    setIsAIChallengeCreating(true);
    setAiChallengeError('');
    setIsAIChallengeOpen(false);

    try {
      const normalizedGoalType = normalizeGoalType(selected?.goalType) || 'fixed';
      const parameters = selected?.parameters && typeof selected.parameters === 'object'
        ? selected.parameters
        : {};
      const settings = selected?.settings && typeof selected.settings === 'object'
        ? selected.settings
        : {};

      const totalGoals = computeTotalGoals(normalizedGoalType, parameters);
      const targetCount = normalizedGoalType === 'fixed' ? Number(parameters?.targetCount || 0) : null;

      const { data: chCreated, error: chCreateError } = await supabase
        .from('ch_challenges')
        .insert({
          project_id: project.id,
          owner_id: user.id,
          title: String(selected?.title || '').trim(),
          description: String(selected?.description || '').trim(),
          tags: Array.isArray(selected?.tags) ? selected.tags : [],
          goal_type: normalizedGoalType,
          target_count: targetCount,
          total_goals: totalGoals,
          default_public: !!settings?.defaultPublic,
          sequential_unlock: !!settings?.sequentialUnlock,
          meta: {
            parameters,
            settings,
          },
        })
        .select('*')
        .single();

      if (chCreateError || !chCreated?.id) {
        setAiChallengeError(chCreateError?.message || '챌린지 생성에 실패했습니다');
        return;
      }

      let challengeCoverPath = '';
      const chFile = selected?.coverImageFile;

      if (chFile) {
        if (!chFile?.type?.startsWith('image/')) {
          setAiChallengeError('이미지 파일만 업로드할 수 있습니다');
          return;
        }

        let blob = chFile;
        if (chFile.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(chFile, 1024 * 1024);
        }

        const path = makeSafeObjectPath({
          userId: user.id,
          originalName: chFile?.name,
          prefix: 'covers',
        });

        const { error: uploadError } = await uploadToBucket({
          bucket: 'challenge-covers',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          setAiChallengeError(uploadError?.message || '이미지 업로드에 실패했습니다');
          return;
        }

        challengeCoverPath = path;
      }

      if (challengeCoverPath) {
        const { error: updateError } = await supabase
          .from('ch_challenges')
          .update({
            cover_image_path: challengeCoverPath,
            cover_image_alt: String(selected?.title || '').trim() || 'Challenge cover image',
          })
          .eq('id', chCreated.id);

        if (updateError) {
          setAiChallengeError(updateError?.message || '챌린지 이미지 저장에 실패했습니다');
          return;
        }
      }

      await loadProject();
    } finally {
      setIsAIChallengeCreating(false);
    }
  }, [loadProject, navigate, project?.id, user?.id]);

  const handleEditProject = async () => {
    if (!project) return;
    setEditErrors({});

    let coverUrl = project?.coverImage || '';
    const coverPath = project?.coverImagePath || '';
    if (!coverUrl && coverPath) {
      coverUrl = await getCachedSignedUrl({ bucket: 'project-covers', path: coverPath });
    }

    setEditForm({
      title: project?.title || '',
      description: project?.description || '',
      coverImage: coverUrl || null,
      coverImageFile: null,
      tagsText: Array.isArray(project?.tags) ? project.tags.join(', ') : '',
      isPublic: !!project?.isPublic,
    });
    setIsEditOpen(true);
  };

  const validateEdit = () => {
    const next = {};
    if (!editForm?.title?.trim()) next.title = '목표 제목을 입력하세요';
    if (!editForm?.description?.trim()) next.description = '목표 설명을 입력하세요';
    setEditErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!validateEdit()) return;
    if (!user?.id || !project?.id) return;

    setIsSavingEdit(true);
    try {
      const previousCoverPath = project?.coverImagePath || '';
      const shouldRemoveCover = !editForm?.coverImage && !editForm?.coverImageFile;

      let coverImagePath = previousCoverPath;

      if (shouldRemoveCover) {
        coverImagePath = '';
      }

      const file = editForm?.coverImageFile;
      if (file) {
        if (!file?.type?.startsWith('image/')) {
          setEditErrors((prev) => ({ ...prev, coverImage: '이미지 파일만 업로드할 수 있습니다' }));
          return;
        }

        let blob = file;
        if (file.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(file, 1024 * 1024);
        }

        const path = makeSafeObjectPath({
          userId: user.id,
          originalName: file?.name,
          prefix: 'covers',
        });

        const { error: uploadError } = await uploadToBucket({
          bucket: 'project-covers',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          setEditErrors((prev) => ({ ...prev, coverImage: uploadError?.message || '이미지 업로드에 실패했습니다' }));
          return;
        }

        coverImagePath = path;
      }

      const { error: updateError } = await supabase
        .from('ch_projects')
        .update({
          title: editForm?.title?.trim(),
          description: editForm?.description?.trim(),
          tags: parseTags(editForm?.tagsText),
          cover_image_path: coverImagePath || null,
          cover_image_alt: editForm?.title?.trim() || 'Project cover image',
          is_public: !!editForm?.isPublic,
        })
        .eq('id', project.id);

      if (updateError) {
        setEditErrors((prev) => ({ ...prev, form: updateError?.message || '목표 수정에 실패했습니다' }));
        return;
      }

      if (previousCoverPath && previousCoverPath !== coverImagePath) {
        await supabase.storage.from('project-covers').remove([previousCoverPath]);
      }

      const coverUrl = coverImagePath
        ? await getCachedSignedUrl({ bucket: 'project-covers', path: coverImagePath })
        : '';

      setProject((prev) => ({
        ...prev,
        title: editForm?.title?.trim(),
        description: editForm?.description?.trim(),
        coverImage: coverUrl,
        coverImageAlt: editForm?.title?.trim() || prev?.coverImageAlt,
        coverImagePath: coverImagePath || '',
        tags: parseTags(editForm?.tagsText),
        isPublic: !!editForm?.isPublic,
      }));

      setIsEditOpen(false);
    } finally {
      setIsSavingEdit(false);
    }
  };

  if (!project && isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary/30 rounded-full mx-auto mb-4" />
          <p className="text-sm text-muted-foreground caption">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!project && !isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <BreadcrumbNavigation />
        <main className="pt-28 lg:pt-28 pb-24 lg:pb-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-card rounded-lg shadow-gaming-md p-6">
              <p className="text-sm text-muted-foreground caption">목표를 찾을 수 없습니다.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={project?.title ? project.title : '목표'}
        description={project?.description ? project.description : undefined}
      />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <ProjectHeader project={project} onEditProject={isOwner ? handleEditProject : undefined} />
          
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground">
                챌린지 목록
              </h2>
              {isOwner && (
                <div className="hidden lg:flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAiChallengeError('');
                      setIsAIChallengeOpen(true);
                    }}
                    disabled={isAIChallengeCreating}
                    className="flex items-center space-x-2 px-6 py-3 border border-border bg-background text-foreground rounded-md shadow-gaming-md transition-gaming hover:shadow-gaming-lg active:translate-y-px"
                  >
                    <span className="text-sm md:text-base font-medium">AI 챌린지 추천</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleAddChallenge}
                    className="flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-md shadow-gaming-md transition-gaming hover:shadow-gaming-lg active:translate-y-px"
                  >
                    <span className="text-sm md:text-base font-medium">새 챌린지 추가</span>
                  </button>
                </div>
              )}
            </div>

            {isOwner && aiChallengeError ? (
              <div className="mb-4 rounded-md border border-destructive/20 bg-destructive/10 p-3">
                <p className="text-xs md:text-sm text-destructive">{aiChallengeError}</p>
              </div>
            ) : null}
            
            {challenges?.length === 0 ? (
              <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
                <p className="text-sm text-muted-foreground caption">
                  아직 등록된 챌린지가 없습니다.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                {challenges?.map((challenge) => (
                  <ChallengeCard
                    key={challenge?.id}
                    challenge={challenge}
                    onClick={() => handleChallengeClick(challenge?.id)}
                  />
                ))}
              </div>
            )}

          </div>

          {challenges?.length > 0 &&
          <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8">
              <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
                목표 팁
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-primary data-text">1</span>
                  </div>
                  <p className="text-sm md:text-base text-muted-foreground">
                    하이라이트된 챌린지는 다음으로 진행할 챌린지입니다. 순차적으로 완료하세요.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-primary data-text">2</span>
                  </div>
                  <p className="text-sm md:text-base text-muted-foreground">
                    잠긴 챌린지는 이전 챌린지를 완료해야 시작할 수 있습니다.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-primary data-text">3</span>
                  </div>
                  <p className="text-sm md:text-base text-muted-foreground">
                    각 챌린지 카드를 클릭하여 상세 정보와 인증 제출 화면으로 이동하세요.
                  </p>
                </div>
              </div>
            </div>
          }
        </div>
      </main>

      {isOwner && isAIChallengeCreating && (
        <div className="fixed inset-0 z-[1000] bg-background/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-gaming-lg px-5 py-4 max-w-sm w-full">
            <div className="text-sm font-heading font-semibold text-foreground">챌린지 등록 중</div>
            <div className="mt-2 text-xs text-muted-foreground caption whitespace-pre-line">
              선택한 챌린지를 생성하고 커버 이미지를 업로드하고 있습니다…
            </div>
            <div className="mt-4 w-full h-2 rounded bg-muted overflow-hidden">
              <div className="h-full w-1/2 bg-primary animate-pulse" />
            </div>
          </div>
        </div>
      )}
      
      {isEditOpen && (
        <div className="fixed inset-0 z-[999] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-lg md:text-xl font-heading font-semibold text-foreground">목표 수정</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">제목/설명/커버 이미지를 수정합니다</div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
              >
                닫기
              </button>
            </div>

            <div className="space-y-4">
              <Input
                type="text"
                label="목표 제목"
                value={editForm?.title}
                onChange={(e) => setEditForm((prev) => ({ ...prev, title: e?.target?.value }))}
                error={editErrors?.title}
                required
                maxLength={50}
              />

              <div>
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  목표 설명 <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={editForm?.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e?.target?.value }))}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                  placeholder="목표 설명"
                />
                {editErrors?.description && (
                  <p className="text-xs text-destructive mt-2">{editErrors.description}</p>
                )}
              </div>

              <Input
                type="text"
                label="태그"
                description="쉼표(,)로 구분하여 입력하세요"
                placeholder="예: 건강, 운동, 습관"
                value={editForm?.tagsText}
                onChange={(e) => setEditForm((prev) => ({ ...prev, tagsText: e?.target?.value }))}
                maxLength={200}
              />

              <Checkbox
                label="공유(랜딩 공개)"
                description="체크하면 랜딩 페이지에 이 목표가 노출됩니다"
                checked={!!editForm?.isPublic}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isPublic: e?.target?.checked }))}
              />

              <CoverImageUpload
                coverImage={editForm?.coverImage}
                headerActions={(
                  <Button
                    variant="outline"
                    iconName="Sparkles"
                    iconPosition="left"
                    onClick={() => setIsAICoverOpen(true)}
                    disabled={isSavingEdit}
                  >
                    AI 커버 이미지 생성
                  </Button>
                )}
                onImageChange={(image, file) => {
                  if (!image || !file) {
                    setEditForm((prev) => ({ ...prev, coverImage: null, coverImageFile: null }));
                    return;
                  }

                  if (!file?.type?.startsWith('image/')) {
                    setEditErrors((prev) => ({ ...prev, coverImage: '이미지 파일만 업로드할 수 있습니다' }));
                    setEditForm((prev) => ({ ...prev, coverImage: null, coverImageFile: null }));
                    return;
                  }

                  setEditErrors((prev) => ({ ...prev, coverImage: '' }));
                  setEditForm((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
                }}
                error={editErrors?.coverImage}
              />

              {editErrors?.form && (
                <p className="text-xs md:text-sm text-destructive">{editErrors?.form}</p>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setIsEditOpen(false)} disabled={isSavingEdit}>
                  취소
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  disabled={isSavingEdit || isDeleting}
                >
                  삭제
                </Button>
                <Button variant="default" iconName="Check" iconPosition="left" onClick={handleSaveEdit} loading={isSavingEdit}>
                  저장
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AICoverImageWizard
        open={isAICoverOpen}
        onClose={() => setIsAICoverOpen(false)}
        title={editForm?.title}
        description={editForm?.description}
        onApply={(image, file) => {
          setEditErrors((prev) => ({ ...prev, coverImage: '' }));
          setEditForm((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
        }}
      />

      <AIChallengeRecommendWizard
        open={isAIChallengeOpen}
        onClose={() => setIsAIChallengeOpen(false)}
        projectTitle={project?.title}
        projectDescription={project?.description}
        onSelect={handleAIChallengeSelect}
      />

      {isEditOpen && isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6">
            <div className="text-lg font-heading font-semibold text-foreground mb-2">목표 삭제</div>
            <p className="text-sm text-muted-foreground caption mb-4">
              이 목표를 삭제하면 하위 챌린지와 모든 인증 데이터 및 이미지가 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
            </p>
            {editErrors?.form && (
              <p className="text-xs md:text-sm text-destructive mb-3">{editErrors?.form}</p>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
                취소
              </Button>
              <Button variant="destructive" onClick={handleDeleteProject} loading={isDeleting}>
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>);

};

export default ProjectDetail;